import { useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, X } from "lucide-react";
import { PortalHeading } from "@/pages/portal/PortalLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg, fileUrl } from "@/lib/api";
import { STATUS_ORDER, slug } from "@/lib/constants";

const JobDialog = ({ job, onClose, reload }) => {
  const [note, setNote] = useState("");
  const [extra, setExtra] = useState({ title: "", amount: "" });
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const upload = (file) => run(async () => {
    const fd = new FormData(); fd.append("file", file); fd.append("purpose", "job_photo");
    const r = await api.post("/files/upload", fd);
    await api.post(`/admin/jobs/${job.reference}/photos`, { file_id: r.data.id, caption: file.name });
  }, "Photo added");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="admin-job-dialog">
        <DialogHeader><DialogTitle className="font-display">{job.reference} · {job.service_title}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#56585E]">{[job.customer_name, job.vehicle?.make, job.vehicle?.model, job.vehicle?.registration].filter(Boolean).join(" · ")}</p>
        <label className="text-sm font-medium">Move to stage
          <select value={job.status} onChange={(e) => run(() => api.patch(`/admin/jobs/${job.reference}/status`, { status: e.target.value }), "Stage updated")} className="mt-1 w-full rounded-md border px-3 py-2 text-sm" data-testid="admin-job-status-select">
            {STATUS_ORDER.map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <div className="flex gap-2"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Customer-visible update" data-testid="admin-job-note-input" />
          <button disabled={!note || busy} onClick={() => run(() => api.post(`/admin/jobs/${job.reference}/notes`, { note }).then(() => setNote("")), "Update posted")} className="rounded-md bg-[#020165] text-white px-4 text-sm" data-testid="admin-job-note-submit">Post</button></div>
        {job.customer_notes?.length > 0 && (
          <ul className="max-h-28 overflow-y-auto space-y-1 text-xs text-[#56585E]" data-testid="admin-job-notes-list">
            {job.customer_notes.map((n, i) => <li key={i} className="border-l-2 border-[#F86900] pl-2">{n}</li>)}
          </ul>
        )}
        <label className="flex items-center gap-2 rounded-md border border-dashed px-3 py-3 text-sm cursor-pointer hover:bg-black/5" data-testid="admin-job-photo-upload">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Upload progress photo
          <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) upload(e.target.files[0]); e.target.value = ""; }} data-testid="admin-job-photo-input" />
        </label>
        {job.progress_photos?.length > 0 && (
          <div className="grid grid-cols-4 gap-2" data-testid="admin-job-photos">
            {job.progress_photos.map((p) => (
              <div key={p.file_id} className="relative group">
                <a href={fileUrl(p.file_id)} target="_blank" rel="noreferrer"><img src={fileUrl(p.file_id)} alt={p.caption || "Progress photo"} className="aspect-square w-full rounded-md object-cover bg-[#F4F4F4]" /></a>
                <button type="button" disabled={busy} onClick={() => window.confirm("Remove this photo? The customer will no longer see it.") && run(() => api.delete(`/admin/jobs/${job.reference}/photos/${p.file_id}`), "Photo removed")}
                  className="absolute top-1 right-1 rounded-full bg-black/70 p-1 text-white hover:bg-red-600" aria-label="Remove photo" data-testid={`admin-job-photo-remove-${p.file_id}`}><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        <div className="rounded-lg bg-[#F4F4F4] p-3 space-y-2">
          <p className="text-sm font-semibold">Request approval for extra work</p>
          <div className="flex gap-2">
            <Input value={extra.title} onChange={(e) => setExtra({ ...extra, title: e.target.value })} placeholder="e.g. Replace front brake pads" data-testid="admin-approval-title-input" />
            <Input value={extra.amount} onChange={(e) => setExtra({ ...extra, amount: e.target.value })} placeholder="USD" className="w-24" data-testid="admin-approval-amount-input" />
          </div>
          <button disabled={!extra.title || !extra.amount || busy} data-testid="admin-approval-submit"
            onClick={() => run(() => api.post("/admin/approvals", { job_reference: job.reference, ...extra }).then(() => setExtra({ title: "", amount: "" })), "Approval sent to customer")}
            className="rounded-full bg-[#F86900] text-[#0D141A] px-4 py-1.5 text-sm font-bold disabled:opacity-40">Send to customer</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminBoard() {
  const { data, reload } = useFetch("/admin/inbox");
  const [openRef, setOpenRef] = useState(null);
  const jobs = data?.jobs || [];
  const open = jobs.find((j) => j.reference === openRef);
  const drop = async (e, status) => {
    const ref = e.dataTransfer.getData("text/plain");
    try { await api.patch(`/admin/jobs/${ref}/status`, { status }); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div data-testid="admin-board">
      <PortalHeading title="Workshop board" sub="Drag a job card to move it, or click to update the customer." />
      <div className="flex gap-3 overflow-x-auto pb-4">
        {STATUS_ORDER.map((s) => (
          <div key={s} onDragOver={(e) => e.preventDefault()} onDrop={(e) => drop(e, s)} data-testid={`admin-board-column-${slug(s)}`} className="w-60 shrink-0 rounded-2xl bg-white/70 p-3">
            <p className="text-xs font-bold uppercase tracking-wider text-[#020165] mb-3">{s} <span className="text-[#56585E]">({jobs.filter((j) => j.status === s).length})</span></p>
            <div className="space-y-2 min-h-[80px]">
              {jobs.filter((j) => j.status === s).map((j) => (
                <button key={j.reference} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", j.reference)} onClick={() => setOpenRef(j.reference)}
                  data-testid={`admin-job-card-${j.reference}`} className="w-full text-left rounded-xl bg-white border p-3 hover:border-[#F86900] transition-colors cursor-grab">
                  <p className="text-[11px] font-bold text-[#F86900]">{j.reference}</p><p className="text-sm font-semibold">{j.service_title}</p>
                  <p className="text-xs text-[#56585E]">{[j.customer_name, j.vehicle?.registration].filter(Boolean).join(" · ")}</p>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {open && <JobDialog job={open} onClose={() => setOpenRef(null)} reload={reload} />}
    </div>
  );
}
